import { PrismicClient, rclient } from '@landsbankinn/prismic';
import * as prismic from '@prismicio/client';
import * as PrismicHelpers from '@prismicio/helpers';
import { Config } from './config';

interface Doc {
  id: string;
  uid: string;
  tags: string[];
  type: string;
  lang: string;
  isBroken: boolean;
}

interface UrlResolverResult {
  href: string;
  as?: string;
  onClick?: any;
}

export enum Language {
  IS = 'is',
  EN = 'en',
}

export enum DocumentType {
  Page = 'page',
  Article = 'article',
  ArticleCategory = 'article_category',
  Author = 'author',
  Discussion = 'discussion',
  Employee = 'employee',
  FaqCategory = 'faq_category',
  FaqItem = 'faq_item',
  Website = 'website',
}

const langLocale = {
  [Language.IS]: 'is',
  [Language.EN]: 'en-gb',
};

const _pages = new Map();
_pages.set('is', []);
_pages.set('en', []);

export interface AllPagesPage {
  id: string;
  uid: string;
  slug?: string;
  parentId?: string;
  title?: string;
  url?: string;
}

/**
 * Fetch a list of all pages on the website
 * @param client ApolloClient
 * @param lang Language to fetch
 */
export async function allPages(
  lang: Language | string = Language.IS,
  ref: string | undefined = undefined,
): Promise<AllPagesPage[]> {
  const locale = (langLocale as any)[lang] || lang;

  if (Config.REDIS_URL !== '') {
    const getAllPages = await rclient.get(`ALL_PAGES_${lang}`);
    if (getAllPages) {
      const result = JSON.parse(getAllPages);
      _pages.set(lang, result);
      return result;
    }
  }

  // pages container
  const pages: any[] = [];
  const prefix = `/${lang.split('-').shift()}`.replace('/is', '');

  let res: any | undefined = undefined;
  while (!res || res.next_page) {
    res = await PrismicClient.query(
      prismic.predicate.at('document.type', 'page'),
      {
        pageSize: 100,
        page: (res?.page || 0) + 1,
        lang: locale,
        ref,
      },
    );
    pages.push(...res.results);
  }

  // process pages
  const result = pages.map((page) => {
    const parts = [];
    let _page = page;
    parts.push(_page?.data?.slug || _page?.uid);
    while (_page?.data?.parent) {
      _page = pages.find((p) => p?.id === _page.data?.parent?.id);
      if (_page) {
        parts.push(_page?.data?.slug || _page?.uid);
      }
    }

    return {
      id: page?.id,
      uid: page?.uid,
      slug: page?.data?.slug,
      parentId: page?.data?.parent?.id ?? null,
      title: PrismicHelpers.asText(page.data?.page_title) ?? undefined,
      url: `${prefix}/${parts.reverse().join('/')}`,
    };
  });

  // set redis cache
  if (Config.REDIS_URL !== '') {
    rclient.set(`ALL_PAGES_${lang}`, JSON.stringify(result), 60 * 2);
  }

  // store pages for the link resolver
  _pages.set(lang, result);
  return result;
}

export async function fetchAllPagesForAllLanguages(ref?: string) {
  await Promise.all([allPages('is', ref), allPages('en', ref)]);
}

export function setPages(pages: any, lang = 'is') {
  _pages.set(lang, pages);
}

/**
 * Generate page URL via list of pages
 * @param doc Prismic Document
 * @param pages Array of all the Pages
 */
function buildPageUrl(doc: Partial<Doc>, pages: any) {
  const parts = [];
  let page = pages.find((p: any) => p.id === doc.id);
  parts.push(page?.slug || page?.uid);
  while (page && page.parentId) {
    page = pages.find((p: any) => p.id === page.parentId);
    if (page) {
      parts.push(page?.slug || page?.uid);
    }
  }
  const slug = parts.reverse().join('/');
  return `/${slug === 'index' ? '' : slug}`;
}

/**
 * Generate page URL via list of pages
 * @param doc Prismic Document
 * @param pages Array of all the Pages
 */
export function buildBreadcrumbs(doc: Partial<Doc>, pages: any) {
  const parts = [];
  let page = pages.find((p: any) => p.id === doc.id);
  parts.push(page);
  while (page && page.parentId) {
    page = pages.find((p: any) => p.id === page.parentId);
    if (page) {
      parts.push(page);
    }
  }
  return parts.reverse().map((part) => {
    return part;
  });
}

/**
 * Parse a document and return parameters for linking to that page.
 * @param doc Prismic Document
 */
export function urlResolver(doc?: Partial<Doc>): UrlResolverResult {
  // No document was found
  if (!doc) {
    return { href: '#nodoc', as: '#nodoc' };
  }
  const docType = doc.type ?? DocumentType.Page;
  const lang = (doc.lang ?? 'is').split('-').shift();
  const prefix = `/${lang}`.replace('/is', '');

  // English pages
  if (lang === 'en') {
    switch (docType) {
      case DocumentType.Page:
        const as = buildPageUrl(doc, _pages.get(lang));
        return {
          href: `${prefix}/[...page]`,
          as: `${prefix}${as}`,
        };
      default:
        return { href: `${prefix}/?type=${doc.type}&id=${doc.id}` };
    }
  }

  switch (docType) {
    case DocumentType.Page:
      const as = buildPageUrl(doc, _pages.get(lang));
      return { href: '/[...page]', as };

    default:
      return { href: `/?type=${doc.type}&id=${doc.id}` };
  }
}

export function linkResolver(doc: Partial<Doc>) {
  const url = urlResolver(doc);
  return url.as;
}
